import { useState, type ReactNode } from 'react'
import { setLabraChatComposerDraft, useLabraChatDispatch } from '@labrav/common'
import type { ChatSamplePrompt } from './flyoutChatPrompts'
import { useStyles } from './ChatSamplePrompts.styles'

const INSERT_HINT =
  'Click to insert this into the prompt box below, then edit as needed.'

const renderPrompt = (prompt: string, emphasis: string): ReactNode => {
  const index = prompt.indexOf(emphasis)
  if (index < 0) return <span className="muted">{prompt}</span>
  const before = prompt.slice(0, index)
  const after = prompt.slice(index + emphasis.length)
  return (
    <>
      {before && <span className="muted">{before}</span>}
      <em>{emphasis}</em>
      {after && <span className="muted">{after}</span>}
    </>
  )
}

interface ChatSamplePromptsProps {
  userName?: string
  prompts: ChatSamplePrompt[]
}

/** Empty-state samples. A click fills the composer so the user can edit first. */
export const ChatSamplePrompts = ({
  userName,
  prompts,
}: ChatSamplePromptsProps) => {
  const { classes, cx } = useStyles()
  const dispatch = useLabraChatDispatch()
  const [hoveredId, setHoveredId] = useState<string | null>(null)

  return (
    <div className={classes.root} data-testid="chat-sample-prompts">
      {userName && <p className={classes.greeting}>Hi {userName} 👋</p>}
      <div className={classes.grid}>
        {prompts.map(sample => (
          <button
            key={sample.id}
            type="button"
            className={cx(
              classes.card,
              hoveredId === sample.id && classes.cardHover
            )}
            onMouseEnter={() => setHoveredId(sample.id)}
            onMouseLeave={() => setHoveredId(null)}
            onFocus={() => setHoveredId(sample.id)}
            onBlur={() => setHoveredId(null)}
            onClick={() => dispatch(setLabraChatComposerDraft(sample.prompt))}
          >
            <span className={classes.text}>
              {renderPrompt(sample.prompt, sample.emphasis)}
            </span>
            {hoveredId === sample.id && (
              <span className={classes.tooltip} role="tooltip">
                {INSERT_HINT}
              </span>
            )}
          </button>
        ))}
      </div>
    </div>
  )
}
